import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createUserMember } from '../services/api.js';
import NotificationPopup from '../components/NotificationPopup.js';
import {
  isValidEmail,
  isValidPassword,
  getPasswordErrorMessage,
} from '../utils/validation.js';
import ReatorBg from '../assets/reator.png';

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    passwordConfirm: '',
    acceptedTerms: false,
    birthDate: '',
    gender: '',
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: '',
  });
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isValidEmail(formData.email)) {
      setNotification({
        show: true,
        message: 'Por favor, insira um e-mail válido.',
        type: 'error',
      });
      return;
    }

    if (!isValidPassword(formData.password)) {
      setNotification({
        show: true,
        message: getPasswordErrorMessage(),
        type: 'error',
      });
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setNotification({
        show: true,
        message: 'As senhas não coincidem',
        type: 'error',
      });
      return;
    }

    if (!formData.acceptedTerms) {
      setNotification({
        show: true,
        message: 'Você deve aceitar os Termos de Uso',
        type: 'error',
      });
      return;
    }

    setLoading(true);
    setNotification(prev => ({ ...prev, show: false }));

    try {
      const userData = {
        nm_usuario: formData.name,
        nm_email: formData.email,
        pw_senha: formData.password,
        id_instituicao: 3,
        sg_aluno: 'S',
        dt_nascimento: formData.birthDate,
        cd_genero: formData.gender,
        bo_liberado: false,
        id_tipo_instituicao: 1,
        id_area_atuacao: 1,
        id_cargo: 1,
      };

      await createUserMember(userData);

      setNotification({
        show: true,
        message:
          'confirme seu usuario no email cadastrado para realizar seu login',
        type: 'success',
      });

      window.setTimeout(() => {
        navigate('/login');
      }, 4000);
    } catch (err) {
      setNotification({
        show: true,
        message: err.message || 'Ocorreu um erro durante o cadastro',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex font-sans text-gray-800 bg-white'>
      <NotificationPopup
        isVisible={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      <div className='hidden md:flex md:w-1/2 relative flex-col justify-center items-center text-white p-12 overflow-hidden'>
        <div
          className='absolute inset-0 z-0'
          style={{
            backgroundImage: `url(${ReatorBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className='absolute inset-0 bg-[#0a2e3d]/40 mix-blend-multiply' />
          <div className='absolute inset-0 bg-gradient-to-tr from-[#0a2e3d] via-transparent to-transparent opacity-40' />
        </div>

        <div className='relative z-10 text-center'>
          <h1 className='text-4xl md:text-5xl font-bold mb-6 leading-tight'>
            Junte-se a nós!
          </h1>
          <p className='text-lg md:text-xl text-gray-200 max-w-md mx-auto'>
            Crie sua conta e e experimente o melhor em conteudos a Operação de
            Reatores Fischer-Tropsch.
          </p>
        </div>
      </div>

      <div className='w-full md:w-1/2 flex items-center justify-center p-8 md:p-12 relative'>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className='w-full max-w-md'
        >
          <div className='mb-8 text-center'>
            <h2 className='text-3xl font-bold text-[#0a2e3d] mb-4'>
              Cadastro Indisponível
            </h2>
            <div className='bg-yellow-50 border-l-4 border-yellow-400 p-6 md:p-8 mt-6 rounded-r-lg shadow-sm'>
              <h3 className='text-lg font-bold text-yellow-800 mb-2'>
                Não é possível criar conta no momento
              </h3>
              <p className='text-yellow-700 text-md'>
                O recurso de cadastro de novas contas será ativado em breve. Por
                favor, aguarde as novidades!
              </p>
            </div>
          </div>

          <div className='mt-8 text-center text-sm text-gray-600'>
            Já tem uma conta?{' '}
            <Link
              to='/login'
              className='text-cyan-600 hover:text-cyan-800 font-bold ml-1 transition-colors'
            >
              Faça login
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignUpPage;
